import React, { useState, useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons'

import { onModalMessage } from 'src/utils/messages/modalChannel'

import GiftAidModalButtons from './GiftAidModalButtons'
import Tooltip from 'src/components/Tooltip'

// UK-only Gift Aid opt-in. Trigger routes through the openModal
// pipeline (data-plg-modal-*) so:
//
//   - Top-frame v6 widget (mode 1) + channel-failed in-iframe
//     fallback (mode 3) hit the local `gift-aid:*` handler
//     registered in donate_v6/openGiftAidModal.js, which opens the
//     inline #giftAidModal BS5 partial. GiftAidModalButtons's
//     React-portaled buttons fire setValue(true) directly via
//     onConfirm. No channel involved.
//   - Properly-embedded v6 (mode 2) — channel.send goes up; nothing
//     intercepts at the host page level → renderModal opens the
//     widgets/modals#gift_aid iframe at the host page. The iframe's
//     "I agree" sends sendToOpener('giftAidConfirmed') which the
//     host forwarder routes back here via the channel client.
//     onModalMessage catches it; same setValue(true).
//
// Two paths, one outcome. The trigger + the subscription don't have
// to know which mode they're in.
const useGiftAid = ({ partnerItem, partner_items, src, id }) => {
  const [ value, setValue ] = useState(false)

  const available = useMemo(() =>
    partnerItem.beneficiary.just_giving || (
      partnerItem.beneficiary.id == '_multiple' &&
      partner_items.some(pi => pi.beneficiary.just_giving)
    )
  , [ partnerItem.external_id ])

  useEffect(() => {
    if (!available) return
    return onModalMessage('giftAidConfirmed', () => setValue(true))
  }, [ available ])

  const render = () =>
    available && (
      <>
        {value ? (
          <div className="d-flex align-items-center">
            <span className="text-success">
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              Opted into Gift Aid
            </span>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none"
            data-plg-modal-label="Gift Aid"
            data-plg-modal-src={src}
            data-plg-modal-id={id}
            data-plg-modal-max-width="36rem"
          >
            Add Gift Aid to your donation
            <Tooltip title="Boost your donation by 25% at no extra cost to you if you're a UK taxpayer. Click to confirm eligibility.">
              <FontAwesomeIcon icon={faInfoCircle} className="ms-1" />
            </Tooltip>
          </button>
        )}
        <GiftAidModalButtons onConfirm={() => setValue(true)} />
      </>
    )

  // Gated the same way useCanadianReceipt.jsx gates its own value
  // (`available && checked`) and matching v7's useGiftAid.jsx fix —
  // value is set by the giftAidConfirmed message and never reset, so
  // without this a donor who opts in and then switches to a non-eligible
  // org would still read as opted-in downstream even though render()
  // already correctly stops showing the confirmed state (it gates on
  // `available` too — this only affects consumers that read `.value`
  // directly: useRecord's order draft, useDonation's collectAddress, and
  // Contact.jsx's address-country prefill). Also exposes `available`
  // itself, which this hook never returned before — nothing destructures
  // a fixed key set off it today, so this is additive.
  return { available, render, value: available && value }
}

export default useGiftAid
